/* global window ApplePaySession */
import React from 'react'
import PropTypes  from 'prop-types'
import { connect } from 'redaction'
import { constants, compose, mixpanel, sentry } from 'common/helpers'
import checkEmptyParams from 'common/helpers/misc/checkEmptyParams'
import actions from 'redux/actions'

import injectGlobals from 'decorators/injectGlobals'

import ApplePayButtonView from '../ApplePayButtonView/ApplePayButtonView'


class BuyApplePayButton extends React.PureComponent {

  static propTypes = {
    emailAddress: PropTypes.string,
    isLoggedIn: PropTypes.bool,
    isTitleBuy: PropTypes.bool,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    couponCode: PropTypes.string,
    className: PropTypes.string,
    placement: PropTypes.string.isRequired,
  }

  static contextTypes = {
    paymentPage: PropTypes.shape({
      getApplePayOrderInfo: PropTypes.func,
      getInquireAction: PropTypes.func,
      applePayBeginAction: PropTypes.func,
      applePayCommitAction: PropTypes.func,
      validateAuthFormFields: PropTypes.func,
    }),
  }

  state = {
    isSupported: false,
  }

  componentDidMount() {
    this.checkApplePaySupport()
  }

  /**
   * lets you find out if there is at least 1 card tied to Apple Pay
   */
  checkApplePaySupport = () => {
    if (window.ApplePaySession) {
      window.ApplePaySession.canMakePaymentsWithActiveCard('merchant.com.scentbird')
        .then((canMakePayments) => {
          if (canMakePayments) {
            this.setState({ isSupported: true })
          }
        })
    }
  }

  rejectApplePaySession = ({ method, error }) => {
    actions.models.ui.hideRequestLoader()

    this.applePaySession[method]({
      errors: [error],
      newTotal: {
        label: 'Scentbird',
        amount: '0.00',
      },
    })
  }

  checkIsUserRegistered = async () => {
    const { isLoggedIn, userValuesFromForm, isRegisterGqlEnabled } = this.props

    if (!isLoggedIn) {
      const { email, password, gender, passwordStrength } = userValuesFromForm

      try {
        await actions.models.user.register({
          values: {
            email: email.value,
            password: password.value,
            gender: gender.value,
            passwordStrength: passwordStrength.value,
          },
          isRegisterGqlEnabled,
        })
      }
      catch (err) {
        const error = new window.ApplePayError('unknown')

        this.rejectApplePaySession({ error, method: 'completeMerchantValidation' })
      }
    }
  }

  fetchCoupon = async (couponCode) => {
    try {
      return await actions.view.paymentPage.fetchDiscount({
        code: couponCode,
        plan: constants.planNames.monthly[1],
        withErrorModal: false,
      })
    }
    catch (error) {
      this.rejectApplePaySession({ error, method: 'completeMerchantValidation' })

      throw new Error(`BuyApplePayButton.fetchCoupon() failed. ${error}`)
    }
  }

  /**
   * Triggered when you open an Apple Pay payment form.
   * In the event handler of this event, it is required to verify the billing session
   */
  onValidateMerchant = async ({ validationURL }) => {
    const { paymentPage: { applePayBeginAction } } = this.context
    const { plan, couponCode } = this.props

    // We have one event for one user when he did not receive a validationURL (SCNT-6092).
    // Need get more info with sentry from frontend side (store, stacktrace, breadcrumbs, etc).
    if (!validationURL) {
      const error = 'BuyApplePayButton.onValidateMerchant() failed. validationURL is empty'

      sentry.captureException(error, {
        extra: { validationURL }, // for check empty type
      })

      return this.rejectApplePaySession({ error, method: 'completeMerchantValidation' })
    }

    actions.models.ui.showRequestLoader()

    await this.checkIsUserRegistered()

    if (couponCode) {
      await this.fetchCoupon(couponCode)
    }

    // send a request for session validation
    try {
      const applePaySession = await applePayBeginAction({
        applePayValidationURL: validationURL,
        paymentType: constants.paymentMethods.applePay,
        plan,
      })

      if (applePaySession) {
        this.applePaySession.completeMerchantValidation(applePaySession)
      }
      else {
        const error = new window.ApplePayError('unknown')

        this.rejectApplePaySession({ error, method: 'completeMerchantValidation' })
        throw new Error(`BuyApplePayButton.onValidateMerchant() failed. applePaySession is empty`)
      }
    }
    catch (err) {
      this.rejectApplePaySession({ error: err, method: 'completeMerchantValidation' })

      throw new Error(`BuyApplePayButton.onValidateMerchant() failed. ${err}`)
    }
  }

  onShippingContactSelected = ({ shippingContact }) => {
    const { paymentPage: { getApplePayOrderInfo, getInquireAction } } = this.context
    const inquireAction = getInquireAction()

    const address = {
      zipCode: shippingContact.postalCode,
      state: shippingContact.administrativeArea,
      city: shippingContact.locality,
      countryCode: shippingContact.countryCode.toLowerCase(),
    }

    if (address.countryCode !== 'us') {
      const error = new window.ApplePayError('addressUnserviceable', 'country')

      return this.rejectApplePaySession({ error, method: 'completeShippingContactSelection' })
    }

    inquireAction(address)
      .then(() => {
        const orderInfo = getApplePayOrderInfo()

        this.applePaySession.completeShippingContactSelection({
          newTotal: orderInfo.total,
          newLineItems: orderInfo.lineItems,
        })
      })
  }

  // triggers when the user confirms payment on the Apple Pay payment form
  // using the Touch ID, Face ID or code. A payment token is available here,
  // which must be transferred to the payment gateway for payment
  onPaymentAuthorized = ({ payment: { token, shippingContact, billingContact } }) => {
    const { paymentPage: { applePayCommitAction } } = this.context

    const {
      locality:           shippingCity,
      givenName:          shippingFirstName,
      familyName:         shippingLastName,
      postalCode:         shippingZipCode,
      addressLines:       shippingStreet,
      administrativeArea: shippingState,
    } = shippingContact

    const {
      locality:           billingCity,
      givenName:          billingFirstName,
      familyName:         billingLastName,
      postalCode:         billingZipCode,
      addressLines:       billingStreet,
      administrativeArea: billingState,
    } = billingContact

    // checking truncated address
    if (!Number.isNaN(Number(shippingStreet[0]))) {
      sentry.captureException('Truncated address shippingStreet', {
        extra: {
          shippingStreet,
        },
      })
    }

    const applePayRequestData = {
      applePayPaymentToken: token,
      shippingFirstName,
      shippingLastName: shippingLastName || shippingFirstName, // SCNT-5347 (shippingLastName & billingLastName is null)
      shippingStreet: shippingStreet[0],
      shippingApt: shippingStreet[1],
      shippingZipCode,
      shippingState,
      shippingCity,

      billingFirstName,
      billingLastName: billingLastName || billingFirstName, // SCNT-5347 (shippingLastName & billingLastName is null)
      billingStreet: billingStreet[0],
      billingApt: billingStreet[1],
      billingZipCode,
      billingState,
      billingCity,
    }

    checkEmptyParams({
      object: applePayRequestData,
      ignoreParams: [ 'shippingApt', 'billingApt' ],
      place: 'BuyApplePayButton applePayRequestData',
      sendError: true,
    })

    applePayCommitAction(this.applePaySession, applePayRequestData)
      .finally(() => actions.models.ui.hideRequestLoader())
  }

  createPaymentRequest = () => {
    const { paymentPage: { getApplePayOrderInfo } } = this.context
    const { emailAddress } = this.props

    const settings = {
      countryCode: 'US',
      currencyCode: 'USD',
      merchantCapabilities: ['supports3DS'],
      supportedNetworks: [ 'amex', 'discover', 'masterCard', 'visa' ],
      requiredShippingContactFields: [ 'postalAddress', 'email' ],
      requiredBillingContactFields: ['postalAddress'],
      shippingContact: {
        emailAddress,
      },
    }

    return {
      ...settings,
      ...getApplePayOrderInfo(),
    }
  }

  initializeApplePaySession = () => {
    // Create apple pay session
    this.applePaySession = new window.ApplePaySession(1, this.createPaymentRequest())
    // Subscribe on session methods
    this.applePaySession.onvalidatemerchant        = this.onValidateMerchant
    this.applePaySession.onshippingcontactselected = this.onShippingContactSelected
    this.applePaySession.onpaymentauthorized       = this.onPaymentAuthorized
    this.applePaySession.oncancel                  = actions.models.ui.hideRequestLoader
    // Start session
    this.applePaySession.begin()
  }

  validateForm = async () => {
    // Get validateAuthFormFields method from Payment Form
    const { paymentPage: { validateAuthFormFields } } = this.context
    const { userValuesFromForm } = this.props
    // Check Auth form values
    const isAuthFormValuesExist = (
      Boolean(userValuesFromForm.email.value)
      && Boolean(userValuesFromForm.password.value)
    )
    // Highlight not valid fields
    // TODO return from validateAuthFormFields is form valid or not valid
    if (!isAuthFormValuesExist) {
      await validateAuthFormFields()
      return false
    }

    return true
  }


  handleClick = async () => {
    const { isLoggedIn, placement } = this.props

    mixpanel.track(constants.analyticsEvents.applePayButton, {
      placement,
    })

    // Check user status (logged in or not logged in)
    if (isLoggedIn) {
      // If user logged in start initialize apple pay session
      this.initializeApplePaySession()
    }
    else {
      // If user not logged in, validate Login or Register form
      const isAuthFormValid = await this.validateForm()

      // If form valid start initialize apple pay session
      if (isAuthFormValid) {
        this.initializeApplePaySession()
      }
    }
  }

  render() {
    const { isSupported } = this.state
    const { withInfo, height, isTitleBuy, className } = this.props

    return isSupported && (
      <ApplePayButtonView
        className={className}
        height={height}
        isTitleBuy={isTitleBuy}
        onClick={this.handleClick}
        withInfo={withInfo}
      />
    )
  }
}


export default compose(
  injectGlobals(({ getCookieValue, getFeatureToggle }) => ({
    planName: (getCookieValue(constants.cookieNames.reactivateSubscription) || {}).planName,
    isRegisterGqlEnabled: getFeatureToggle(constants.gqlFeatures.registerGQL),
  })),
  connect({
    isLoggedIn: 'models.me.isLoggedIn',
    emailAddress: (state, { userValuesFromForm }) => {
      const { models: { me: { isLoggedIn, email } } } = state

      return isLoggedIn ? email : userValuesFromForm.email.value
    },
    plan: (state, props) => {
      const { view: { paymentPage: { selectedPlan } } } = state
      const { planName } = props

      return planName || selectedPlan
    },
  }),
)(BuyApplePayButton)
